import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../../context";
import { useTranslation } from "react-i18next";
import useFetchObjects from "../../../../api/useFetchObjects";
import axios from "axios";
import PatientSearch from "../../../../Components/PatientSearch";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Autocomplete,
  MenuItem,
} from "@mui/material";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import moment from "moment";

const baseUrl = () => process.env.REACT_APP_API_URL || "";
const PAYMENT_METHODS = [
  { value: 1, label: "Cash" },
  { value: 2, label: "Card" },
  { value: 3, label: "Bank Transfer" },
  { value: 9, label: "Other" },
];

export default function AddPayment() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const queryClient = useQueryClient();
  const [patient, setPatient] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [invoiceSearch, setInvoiceSearch] = useState("");
  const [paymentDate, setPaymentDate] = useState(moment().format("YYYY-MM-DDTHH:mm"));
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState(1);
  const [receiptNumber, setReceiptNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const { data: invoicesData } = useFetchObjects(
    ["invoices-for-payment", patient?.id],
    patient?.id ? `invoices/?patient=${patient.id}&page_size=50` : "",
    token,
    { enabled: !!patient?.id }
  );
  const invoices = invoicesData?.results || invoicesData || [];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!patient?.id) {
      setError(t("finance.selectPatient", { defaultValue: "Please select a patient." }));
      return;
    }
    const amt = parseFloat(amount);
    if (isNaN(amt) || amt <= 0) {
      setError(t("finance.validAmount", { defaultValue: "Please enter a valid amount." }));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${baseUrl()}/payments/`,
        {
          patient: patient.id,
          invoice: invoice?.id || null,
          payment_date: paymentDate,
          amount: amt,
          payment_method: paymentMethod,
          receipt_number: receiptNumber,
          notes,
        },
        {
          headers: { Authorization: `Token ${token}`, "Content-Type": "application/json" },
          skipGlobalErrorHandler: true,
        }
      );
      queryClient.invalidateQueries(["payments"]);
      queryClient.invalidateQueries(["invoices"]);
      toast.success(t("finance.paymentRecorded", { defaultValue: "Payment recorded." }));
      navigate("/finance/payments");
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data?.message || err.message);
      toast.error(err.response?.data?.detail || "Failed to record payment");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/finance/payments")} sx={{ mb: 2 }}>
        {t("finance.back", { defaultValue: "Back" })}
      </Button>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: "bold", mb: 3 }}>
          {t("finance.addPayment", { defaultValue: "Add Payment" })}
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <PatientSearch
                value={patient}
                onChange={(v) => { setPatient(v); setInvoice(null); }}
                label={t("finance.patient")}
                placeholder={t("finance.search", "Search...")}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={invoices}
                getOptionLabel={(o) => (o ? `#${o.id} - ${o.patient_name_display || ""} - ${Number(o.total_amount || 0).toFixed(2)}` : "")}
                value={invoice}
                onInputChange={(e, v) => setInvoiceSearch(v)}
                onChange={(e, v) => setInvoice(v)}
                renderInput={(params) => (
                  <TextField {...params} label={t("finance.invoice", "Invoice (optional)")} placeholder={t("finance.optional", "Optional")} />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                type="datetime-local"
                label={t("finance.paymentDate", "Payment Date")}
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                inputProps={{ min: 0.01, step: 0.01 }}
                label={t("finance.amount")}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                select
                label={t("finance.method", "Payment Method")}
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(Number(e.target.value))}
              >
                {PAYMENT_METHODS.map((m) => (
                  <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label={t("finance.receiptNumber", "Receipt Number")}
                value={receiptNumber}
                onChange={(e) => setReceiptNumber(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                multiline
                rows={2}
                label={t("finance.notes")}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </Grid>
          </Grid>
          <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
            <Button type="submit" variant="contained" disabled={submitting}>
              {submitting ? <CircularProgress size={24} /> : t("finance.save", "Save")}
            </Button>
            <Button variant="outlined" onClick={() => navigate("/finance/payments")}>
              {t("finance.cancel", "Cancel")}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
}
